import React from 'react';
import { Download, Mail, Scale, Eye, Lightbulb, ShieldCheck, Lock, UserCheck } from 'lucide-react';

const ICONS = [UserCheck, Eye, Lightbulb, Scale, ShieldCheck, Lock];

// Hero for policy pages that define `hero` (e.g. Responsible AI)
export const PolicyHero = ({ policy, slug }) => {
  const h = policy.hero;
  const downloadMarkdown = () => {
    const meta = policy.meta.map(([k, v]) => `**${k}:** ${v}`).join('  \n');
    const blob = new Blob([`# ${policy.title}\n\n${meta}\n\n${policy.body}`], { type: 'text/markdown' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${slug}.md`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  };

  return (
    <section className="mb-12" data-testid="policy-hero">
      <p className="text-xs uppercase tracking-[0.2em] text-navigate-gold mb-4" data-testid="policy-hero-eyebrow">{h.eyebrow}</p>
      <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight" data-testid="policy-title">
        {h.headline[0]}<br /><span className="text-navigate-gold">{h.headline[1]}</span>
      </h1>
      <p className="mt-6 max-w-3xl text-base md:text-lg text-zinc-300 leading-relaxed">{h.intro}</p>
      <div className="mt-6 flex flex-wrap gap-2">
        {h.badges.map((b) => (
          <span key={b} className="text-xs px-3 py-1 rounded-full border border-zinc-700 text-zinc-300">{b}</span>
        ))}
      </div>
      <div className="mt-6 flex flex-wrap gap-3">
        <button type="button" onClick={downloadMarkdown} data-testid="policy-download-md"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-navigate-gold text-black font-semibold hover:bg-navigate-gold-light transition-colors">
          <Download className="h-4 w-4" /> Download as Markdown
        </button>
        <a href={`mailto:${h.contactEmail}?subject=${encodeURIComponent('Responsible AI - Governance Office')}`} data-testid="policy-contact-governance"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-zinc-700 text-white hover:border-navigate-gold transition-colors">
          <Mail className="h-4 w-4" /> Write to the Governance Office
        </a>
      </div>

      <h2 className="font-heading text-xl md:text-2xl font-bold text-white mt-14 mb-1">Our six commitments</h2>
      <p className="text-sm text-zinc-400 mb-6">The pillars every Altara AI surface is built on.</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="policy-commitments">
        {h.commitments.map(([title, text], i) => {
          const Icon = ICONS[i % ICONS.length];
          return (
            <div key={title} className="p-5 rounded-2xl bg-zinc-900/60 border border-zinc-800 hover:border-navigate-gold/50 transition-colors" data-testid={`policy-commitment-${i}`}>
              <Icon className="h-6 w-6 text-navigate-gold mb-3" />
              <h3 className="font-semibold text-white mb-1">{title}</h3>
              <p className="text-sm text-zinc-400 leading-relaxed">{text}</p>
            </div>
          );
        })}
      </div>

      <div className="mt-8 flex flex-wrap items-center gap-2" data-testid="policy-frameworks">
        <span className="text-xs uppercase tracking-wider text-zinc-500 mr-2">Aligned with</span>
        {h.frameworks.map((f) => (
          <span key={f} className="text-xs px-3 py-1 rounded-md bg-zinc-800 text-zinc-200">{f}</span>
        ))}
      </div>
    </section>
  );
};

export default PolicyHero;
